import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CheckCircle2, Share2 } from "lucide-react";

const MV_ORDER = [
  { key: "trust", title: "Trust" },
  { key: "follow", title: "Follow" },
  { key: "imitate", title: "Imitate" },
  { key: "teach", title: "Teach Others" },
];

function ScoreRow({ title, score, label }) {
  const pct = Math.max(0, Math.min(25, score)) / 25;
  return (
    <div data-testid={`starting-score-${title.toLowerCase().split(' ')[0]}`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="font-serif text-lg">{title}</div>
        <div className="text-sm text-muted-foreground">
          <span className="font-serif text-xl text-foreground tabular-nums mr-2">{score}</span>
          <span className="text-xs uppercase tracking-widest">/ 25 · {label}</span>
        </div>
      </div>
      <div className="h-2 bg-secondary/60 overflow-hidden">
        <div className="h-full bg-primary transition-all" style={{ width: `${pct * 100}%` }} />
      </div>
    </div>
  );
}

export default function AssessmentStartResults() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [content, setContent] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [doc, c] = await Promise.all([
          api.get("/assessment/start"),
          api.get("/assessment/content"),
        ]);
        if (!doc.data?.locked) {
          nav("/app/assessment/start", { replace: true });
          return;
        }
        // Compute movement scores client-side for display using content layout
        const answers = doc.data.answers || [];
        const movements = {};
        c.data.movements.forEach((mv) => {
          const total = mv.statements.reduce((sum, s) => sum + (answers[s.index - 1] || 0), 0);
          const label = total >= 21 ? "Flourishing" : total >= 16 ? "Growing" : total >= 11 ? "Taking root" : "Just beginning";
          movements[mv.key] = { score: total, title: mv.title, label };
        });
        const overall = answers.reduce((a, b) => a + b, 0);
        setData({ answers, overall, movements });
        setSharing(!!doc.data.share_with_leader);
        setContent(c.data);
      } catch {
        nav("/app/assessment/start", { replace: true });
      }
    })();
  }, [nav]);

  const toggleShare = async (next) => {
    try {
      await api.post("/assessment/start/share", { share: next });
      setSharing(next);
      toast.success(next ? "Shared with your group leader." : "Sharing turned off.");
    } catch {
      toast.error("Could not update sharing right now.");
    }
  };

  if (!data || !content) return <div className="py-20 text-center text-muted-foreground">Preparing…</div>;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 text-primary mb-6">
        <CheckCircle2 className="w-5 h-5" />
        <p className="text-xs uppercase tracking-[0.2em]">Starting Assessment · Locked in</p>
      </div>
      <h1 className="font-serif text-5xl leading-tight">Your starting line.</h1>
      <p className="mt-6 text-muted-foreground max-w-2xl font-serif italic leading-relaxed">
        {content.closing_line_start}
      </p>

      <section className="mt-12 space-y-8" data-testid="starting-scores">
        {MV_ORDER.map((mv) => {
          const row = data.movements[mv.key];
          if (!row) return null;
          return <ScoreRow key={mv.key} title={mv.title} score={row.score} label={row.label} />;
        })}
      </section>

      <section className="mt-14 border-t border-border pt-10 text-sm text-muted-foreground max-w-2xl">
        <p>
          We'll compare these numbers side-by-side with your Week 11 answers when you finish the eleven weeks.
          Until then, trust the slow work — the way Scripture compares it, a tree planted by water.
        </p>
      </section>

      <section className="mt-10 border border-border bg-card p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="start-share-section">
        <div className="flex items-start gap-3">
          <Share2 className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <div className="font-serif text-lg">Share with my leader</div>
            <div className="text-sm text-muted-foreground max-w-md">
              Off by default. If turned on, your group leader can see these scores and reflections.
            </div>
          </div>
        </div>
        <Switch data-testid="start-share-toggle" checked={sharing} onCheckedChange={toggleShare} />
      </section>

      <div className="mt-12 flex gap-3 flex-wrap">
        <Button asChild className="rounded-full" data-testid="assessment-start-to-week1">
          <Link to="/app/weeks/1">Begin Week 1 →</Link>
        </Button>
        <Button asChild variant="outline" className="rounded-full">
          <Link to="/app">Back to all weeks</Link>
        </Button>
      </div>
    </div>
  );
}
